import React, { useState, useEffect, useMemo } from 'react'
import { motion } from 'framer-motion'
import {
  DollarSign,
  Plus,
  Edit3,
  Trash2,
  Calendar,
  TrendingUp,
  BarChart3,
  Loader2,
} from 'lucide-react'
import { useDividendStore, DividendRecord } from '../store/dividendStore'
import { usePortfolioStore } from '../store/portfolioStore'
import { useThemeStore } from '../store/themeStore'
import { formatCurrency } from '../lib/utils'
import StockLogo from './StockLogo'
import AddDividendModal from './AddDividendModal'
import { buttonClass } from './ui/Button';

const MASK = '••••••'

type SubTab = 'history' | 'yearly'

const DividendTracker: React.FC = () => {
  const { dividends, loading, loadDividends, deleteDividend } = useDividendStore()
  const { displayCurrency, exchangeRate, getCurrentPrice } = usePortfolioStore()
  const { maskAmounts } = useThemeStore()

  const [activeTab, setActiveTab] = useState<SubTab>('history')
  const [showModal, setShowModal] = useState(false)
  const [editing, setEditing] = useState<DividendRecord | null>(null)
  const [deletingId, setDeletingId] = useState<number | null>(null)

  const m = (v: string) => (maskAmounts ? MASK : v)

  useEffect(() => { loadDividends() }, [])

  const toDisplay = (amount: number, currency: 'USD' | 'THB') => {
    if (displayCurrency === 'USD') {
      return currency === 'USD' ? amount : amount / exchangeRate
    }
    return currency === 'THB' ? amount : amount * exchangeRate
  }

  const totalReceived = useMemo(
    () => dividends.reduce((sum, d) => sum + toDisplay(d.total_received, d.currency), 0),
    [dividends, displayCurrency, exchangeRate]
  )

  const currentYear = new Date().getFullYear()
  const thisYearTotal = useMemo(
    () => dividends
      .filter(d => new Date(d.ex_date).getFullYear() === currentYear)
      .reduce((sum, d) => sum + toDisplay(d.total_received, d.currency), 0),
    [dividends, displayCurrency, exchangeRate, currentYear]
  )

  const yearlySummary = useMemo(() => {
    const map = new Map<number, { total: number; bySymbol: Map<string, { name: string; total: number; count: number }> }>()
    for (const d of dividends) {
      const year = new Date(d.ex_date).getFullYear()
      const amount = toDisplay(d.total_received, d.currency)
      if (!map.has(year)) map.set(year, { total: 0, bySymbol: new Map() })
      const yr = map.get(year)!
      yr.total += amount
      if (!yr.bySymbol.has(d.symbol)) yr.bySymbol.set(d.symbol, { name: d.name, total: 0, count: 0 })
      const sym = yr.bySymbol.get(d.symbol)!
      sym.total += amount
      sym.count++
    }
    return Array.from(map.entries())
      .sort(([a], [b]) => b - a)
      .map(([year, data]) => ({
        year,
        total: data.total,
        stocks: Array.from(data.bySymbol.entries())
          .map(([symbol, v]) => ({ symbol, ...v }))
          .sort((a, b) => b.total - a.total),
      }))
  }, [dividends, displayCurrency, exchangeRate])

  const dividendYields = useMemo(() => {
    const map = new Map<string, number>()
    const cutoff = new Date()
    cutoff.setFullYear(cutoff.getFullYear() - 1)
    for (const d of dividends) {
      if (new Date(d.ex_date) >= cutoff) {
        map.set(d.symbol, (map.get(d.symbol) || 0) + d.amount_per_share)
      }
    }
    return map
  }, [dividends])

  const getYield = (symbol: string, portApiType: string) => {
    const totalDiv = dividendYields.get(symbol)
    if (!totalDiv) return null
    const currency = portApiType === 'us_stock' || portApiType === 'us_fund' ? 'USD' : 'THB'
    const price = getCurrentPrice(symbol, currency)
    if (!price) return null
    return (totalDiv / price) * 100
  }

  const handleDelete = async (id: number) => {
    setDeletingId(id)
    try { await deleteDividend(id) } finally { setDeletingId(null) }
  }

  const summaryCards = [
    { label: 'รับทั้งหมด', value: m(formatCurrency(totalReceived, displayCurrency)), icon: <DollarSign className="w-4 h-4 text-emerald-500" />, color: 'text-gain' },
    { label: `ปี ${currentYear}`, value: m(formatCurrency(thisYearTotal, displayCurrency)), icon: <Calendar className="w-4 h-4 text-blue-500" />, color: 'text-blue-600 dark:text-blue-400' },
    { label: 'จำนวนรายการ', value: String(dividends.length), icon: <BarChart3 className="w-4 h-4 text-purple-500" />, color: 'text-purple-600 dark:text-purple-400' },
  ]

  return (
    <>
      {/* Header — matches Watchlist p-6 header */}
      <div className="p-6 border-b border-gray-100 dark:border-gray-700/50">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-emerald-500" />
            เงินปันผล
          </h2>
          <button
            onClick={() => { setEditing(null); setShowModal(true) }}
            className={buttonClass('primary', 'sm')}
          >
            <Plus className="w-4 h-4 mr-1.5" />
            บันทึกปันผล
          </button>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-3 gap-3">
          {summaryCards.map(card => (
            <div key={card.label} className="flex items-center gap-3 rounded-xl px-4 py-3 bg-gray-50 dark:bg-gray-700/40 border border-gray-100 dark:border-gray-700/50">
              <div className="p-1.5 rounded-lg bg-white dark:bg-gray-600/50 shadow-sm">{card.icon}</div>
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400 leading-none mb-0.5">{card.label}</p>
                <p className={`text-sm font-bold tabular-nums ${card.color}`}>{card.value}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Sub-tab bar — underline style matching parent card */}
      <div className="flex border-b border-gray-100 dark:border-gray-700/50">
        {([
          { id: 'history' as SubTab, label: 'ประวัติ', icon: <Calendar className="w-4 h-4" /> },
          { id: 'yearly' as SubTab, label: 'สรุปรายปี', icon: <TrendingUp className="w-4 h-4" /> },
        ]).map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition-colors -mb-px ${
              activeTab === tab.id
                ? 'border-emerald-500 text-gain'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
        </div>
      ) : dividends.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center border-t border-gray-100 dark:border-gray-700/50">
          <div className="w-16 h-16 rounded-2xl bg-emerald-50 dark:bg-emerald-900/30 flex items-center justify-center mb-4">
            <DollarSign className="w-8 h-8 text-emerald-400" />
          </div>
          <h3 className="text-base font-semibold text-gray-700 dark:text-gray-300 mb-1">ยังไม่มีรายการเงินปันผล</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-5">กดปุ่ม "บันทึกปันผล" เพื่อเพิ่มรายการแรก</p>
          <button
            onClick={() => { setEditing(null); setShowModal(true) }}
            className={buttonClass('primary', 'sm')}
          >
            <Plus className="w-4 h-4 mr-2" />
            บันทึกปันผล
          </button>
        </div>
      ) : activeTab === 'history' ? (
        <HistoryTab
          dividends={dividends}
          m={m}
          displayCurrency={displayCurrency}
          toDisplay={toDisplay}
          getYield={getYield}
          onEdit={r => { setEditing(r); setShowModal(true) }}
          onDelete={handleDelete}
          deletingId={deletingId}
        />
      ) : (
        <YearlyTab
          yearlySummary={yearlySummary}
          m={m}
          displayCurrency={displayCurrency}
        />
      )}

      <AddDividendModal
        isOpen={showModal}
        onClose={() => { setShowModal(false); setEditing(null) }}
        editing={editing}
      />
    </>
  )
}

// ── History Tab ────────────────────────────────────────────────────────────────
interface HistoryTabProps {
  dividends: DividendRecord[]
  m: (v: string) => string
  displayCurrency: 'USD' | 'THB'
  toDisplay: (amount: number, currency: 'USD' | 'THB') => number
  getYield: (symbol: string, portApiType: string) => number | null
  onEdit: (r: DividendRecord) => void
  onDelete: (id: number) => void
  deletingId: number | null
}

const HistoryTab: React.FC<HistoryTabProps> = ({ dividends, m, displayCurrency, toDisplay, getYield, onEdit, onDelete, deletingId }) => {
  return (
    <div>
      {/* Table header — matches Watchlist gradient header */}
      <div className="hidden sm:grid grid-cols-[1fr_120px_100px_120px_80px_110px_60px] items-center gap-x-4 px-6 py-3 table-head">
        <span>หุ้น</span>
        <span className="text-right">ปันผล/หุ้น</span>
        <span className="text-right">จำนวนหุ้น</span>
        <span className="text-right">รับรวม</span>
        <span className="text-center">Yield TTM</span>
        <span className="text-center">วัน XD / จ่าย</span>
        <span />
      </div>

      {/* Rows */}
      <div>
        {dividends.map((d, i) => {
          const yld = getYield(d.symbol, d.port_api_type)
          const displayTotal = toDisplay(d.total_received, d.currency)
          return (
            <motion.div
              key={d.id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.03 }}
              className="group/row grid grid-cols-[1fr_auto] sm:grid-cols-[1fr_120px_100px_120px_80px_110px_60px] gap-x-4 items-center px-6 py-3.5 border-b border-gray-100 dark:border-gray-700/40 border-l-2 border-l-transparent hover:border-l-emerald-400 hover:bg-emerald-50/30 dark:hover:bg-emerald-900/10 transition-all duration-150"
            >
              {/* Symbol */}
              <div className="flex items-center gap-3 min-w-0">
                <StockLogo symbol={d.symbol} size={32} />
                <div className="min-w-0">
                  <p className="text-sm font-bold text-gray-900 dark:text-white group-hover/row:text-emerald-600 dark:group-hover/row:text-emerald-400 transition-colors">{d.symbol}</p>
                  <p className="text-xs text-gray-400 truncate">{d.name}</p>
                </div>
              </div>

              {/* Mobile: total + date stacked */}
              <div className="flex flex-col items-end gap-0.5 sm:hidden">
                <span className="text-sm font-bold text-gain tabular-nums">
                  {m(formatCurrency(displayTotal, displayCurrency))}
                </span>
                <span className="text-xs text-gray-400">
                  {new Date(d.ex_date).toLocaleDateString('th-TH', { day: '2-digit', month: 'short', year: '2-digit' })}
                </span>
              </div>

              {/* ปันผล/หุ้น */}
              <div className="hidden sm:block text-right">
                <span className="text-xs text-gray-600 dark:text-gray-300 tabular-nums">
                  {m(`${d.amount_per_share.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 })} ${d.currency}`)}
                </span>
              </div>

              {/* จำนวนหุ้น */}
              <div className="hidden sm:block text-right">
                <span className="text-xs text-gray-600 dark:text-gray-300 tabular-nums">
                  {d.shares_held.toLocaleString('en-US', { maximumFractionDigits: 2 })}
                </span>
              </div>

              {/* รับรวม */}
              <div className="hidden sm:block text-right">
                <span className="text-sm font-bold text-gain tabular-nums">
                  {m(formatCurrency(displayTotal, displayCurrency))}
                </span>
              </div>

              {/* Yield TTM */}
              <div className="hidden sm:flex justify-center">
                {yld != null ? (
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-md tabular-nums ${
                    yld >= 5
                      ? 'bg-emerald-50 dark:bg-emerald-900/30 text-gain'
                      : yld >= 2
                        ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                  }`}>
                    {yld.toFixed(2)}%
                  </span>
                ) : (
                  <span className="text-xs text-gray-300 dark:text-gray-400">—</span>
                )}
              </div>

              {/* วัน XD / จ่าย */}
              <div className="hidden sm:flex flex-col items-center gap-0.5">
                <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                  {new Date(d.ex_date).toLocaleDateString('th-TH', { day: '2-digit', month: 'short', year: '2-digit' })}
                </span>
                {d.pay_date && (
                  <span className="text-[10px] text-gray-400 dark:text-gray-400">
                    {new Date(d.pay_date).toLocaleDateString('th-TH', { day: '2-digit', month: 'short', year: '2-digit' })}
                  </span>
                )}
              </div>

              {/* Actions */}
              <div className="hidden sm:flex items-center gap-0.5 opacity-0 group-hover/row:opacity-100 transition-opacity">
                <button
                  onClick={() => onEdit(d)}
                  className="p-1 rounded text-gray-300 hover:text-emerald-500 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 transition-all"
                  title="แก้ไข"
                >
                  <Edit3 className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => onDelete(d.id)}
                  disabled={deletingId === d.id}
                  className="p-1 rounded text-gray-300 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all"
                  title="ลบ"
                >
                  {deletingId === d.id
                    ? <div className="w-3.5 h-3.5 rounded-full border border-current border-t-transparent animate-spin" />
                    : <Trash2 className="w-3.5 h-3.5" />
                  }
                </button>
              </div>
            </motion.div>
          )
        })}
      </div>
    </div>
  )
}

// ── Yearly Tab ─────────────────────────────────────────────────────────────────
interface YearlyTabProps {
  yearlySummary: { year: number; total: number; stocks: { symbol: string; name: string; total: number; count: number }[] }[]
  m: (v: string) => string
  displayCurrency: 'USD' | 'THB'
}

const YearlyTab: React.FC<YearlyTabProps> = ({ yearlySummary, m, displayCurrency }) => {
  const [expandedYear, setExpandedYear] = useState<number | null>(yearlySummary[0]?.year ?? null)

  if (yearlySummary.length === 0) {
    return <p className="text-center py-8 text-sm text-gray-400 dark:text-gray-400">ไม่มีข้อมูล</p>
  }

  return (
    <div className="divide-y divide-gray-100 dark:divide-gray-700/50">
      {yearlySummary.map(yr => (
        <div key={yr.year}>
          {/* Year row */}
          <button
            onClick={() => setExpandedYear(expandedYear === yr.year ? null : yr.year)}
            className="w-full flex items-center justify-between px-6 py-4 hover:bg-gray-50 dark:hover:bg-gray-700/20 transition-colors"
          >
            <div className="flex items-center gap-3">
              <span className="text-base font-bold text-gray-900 dark:text-white">{yr.year}</span>
              <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400">
                {yr.stocks.length} หุ้น
              </span>
            </div>
            <span className="text-base font-bold text-gain tabular-nums">
              {m(formatCurrency(yr.total, displayCurrency))}
            </span>
          </button>

          {/* Stock breakdown */}
          {expandedYear === yr.year && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="bg-gray-50/50 dark:bg-gray-700/10"
            >
              {yr.stocks.map(s => {
                const pct = yr.total > 0 ? (s.total / yr.total) * 100 : 0
                return (
                  <div
                    key={s.symbol}
                    className="flex items-center justify-between px-8 py-2.5 border-t border-gray-100 dark:border-gray-700/40"
                  >
                    <div>
                      <p className="text-sm font-medium text-gray-800 dark:text-gray-200">{s.symbol}</p>
                      <p className="text-xs text-gray-400 truncate max-w-[200px]">{s.name}</p>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="hidden sm:flex items-center gap-2">
                        <div className="h-1.5 rounded-full w-24 bg-gray-200 dark:bg-gray-600">
                          <div className="h-full rounded-full bg-gain-gradient" style={{ width: `${pct}%` }} />
                        </div>
                        <span className="text-xs w-9 text-right text-gray-400 tabular-nums">{pct.toFixed(0)}%</span>
                      </div>
                      <span className="text-sm font-semibold text-gain tabular-nums">
                        {m(formatCurrency(s.total, displayCurrency))}
                      </span>
                    </div>
                  </div>
                )
              })}
            </motion.div>
          )}
        </div>
      ))}
    </div>
  )
}

export default DividendTracker
